import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiResource } from "@/lib/api/types"
import type { InvolvementFamily } from "@/types/catalogue"
import type {
  Appointment,
  ApiAppointment,
  IndividualAppointments,
  NominationInput,
  NominationOffer,
  RemoveAppointmentInput,
  UpdateAppointmentInput,
} from "@/types/appointment"

export function toAppointment(data: ApiAppointment): Appointment {
  return {
    id: data.id,
    firm: { id: data.firm.id, legalName: data.firm.legal_name },
    individual: {
      id: data.individual.id,
      name: data.individual.name,
      email: data.individual.email,
      complianceScore: data.individual.compliance_score,
      lastSignedInAt: data.individual.last_signed_in_at,
      waitingOn: data.individual.waiting_on,
    },
    you: data.you,
    jobTitle: data.job_title,
    role: data.role,
    roles: data.roles,
    isContact: data.is_contact,
    approvedPerson: data.approved_person,
    status: data.status,
    statusLabel: data.status_label,
    live: data.live,
    onboarding: data.onboarding,
    startedOn: data.started_on,
    endedOn: data.ended_on,
    statusChangedAt: data.status_changed_at,
    removedOutsideProcess: data.removed_outside_process,
    voided: data.voided,
    functions: data.functions.map((held) => ({
      id: held.id,
      involvementType: held.involvement_type,
      label: held.label,
      code: held.code,
      startedOn: held.started_on,
      approvedOn: held.approved_on,
      endedOn: held.ended_on,
      status: held.status,
      statusLabel: held.status_label,
      end: held.end,
    })),
    changes: data.changes,
    standing: data.standing,
  }
}

function blank(value: string): string | null {
  const trimmed = value.trim()

  return trimmed === "" ? null : trimmed
}

function toNominationPayload(input: NominationInput) {
  const startedOn = blank(input.startedOn)

  return {
    first_name: input.firstName.trim(),
    middle_names: blank(input.middleNames ?? ""),
    last_name: input.lastName.trim(),
    email: input.email.trim(),
    job_title: blank(input.jobTitle),
    is_contact: input.isContact,
    started_on: startedOn,
    functions: input.functions.map((involvementType) => ({
      involvement_type: involvementType,
      started_on: startedOn,
    })),
  }
}

export const appointmentQueries = {
  detail: (appointmentId: string) =>
    queryOptions({
      queryKey: apiKey(`/appointments/${appointmentId}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiAppointment>>(
          `/appointments/${appointmentId}`,
          { signal }
        )

        return toAppointment(data)
      },
    }),

  listForFirm: (firmId: string) =>
    queryOptions({
      ...firmAppointments(firmId),
      select: (listing) => listing.appointments,
    }),

  nominationOffer: (firmId: string) =>
    queryOptions({
      ...firmAppointments(firmId),
      select: (listing) => listing.nomination,
    }),

  listForIndividual: (individualId: string) =>
    queryOptions({
      queryKey: apiKey(`/individuals/${individualId}/appointments`),
      queryFn: async ({ signal }): Promise<IndividualAppointments> => {
        const { data, meta } = await apiFetch<{
          data: ApiAppointment[]
          meta: Omit<IndividualAppointments, "appointments">
        }>(`/individuals/${individualId}/appointments`, { signal })

        return { appointments: data.map(toAppointment), ...meta }
      },
    }),

  nominatableFunctions: (appointmentId: string) =>
    queryOptions({
      queryKey: apiKey(`/appointments/${appointmentId}/nominatable-functions`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<{ data: InvolvementFamily[] }>(
          `/appointments/${appointmentId}/nominatable-functions`,
          { signal }
        )

        return data
      },
    }),
}

function firmAppointments(firmId: string) {
  return queryOptions({
    queryKey: apiKey(`/firms/${firmId}/appointments`),
    queryFn: async ({ signal }) => {
      const { data, meta } = await apiFetch<{
        data: ApiAppointment[]
        meta: { nomination: NominationOffer }
      }>(`/firms/${firmId}/appointments`, { signal })

      return {
        appointments: data.map(toAppointment),
        nomination: meta.nomination,
      }
    },
  })
}

function store(
  queryClient: ReturnType<typeof useQueryClient>,
  appointments: Appointment[]
) {
  for (const appointment of appointments) {
    queryClient.setQueryData(
      apiKey(`/appointments/${appointment.id}`),
      appointment
    )
  }
}

export function useNominate(firmId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: NominationInput) => {
      const { data } = await apiFetch<ApiResource<ApiAppointment>>(
        `/firms/${firmId}/appointments`,
        { method: "POST", body: toNominationPayload(input) }
      )

      return toAppointment(data)
    },
    onSuccess: (appointment) => {
      store(queryClient, [appointment])
    },
  })
}

export function useUpdateAppointment(appointmentId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateAppointmentInput) => {
      const { data } = await apiFetch<ApiResource<ApiAppointment>>(
        `/appointments/${appointmentId}`,
        { method: "PATCH", body: toUpdatePayload(input) }
      )

      return toAppointment(data)
    },
    onSuccess: (appointment) => {
      store(queryClient, [appointment])
    },
  })
}

export function useRemoveAppointment(appointmentId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: RemoveAppointmentInput) => {
      const { data } = await apiFetch<ApiResource<ApiAppointment>>(
        `/appointments/${appointmentId}`,
        {
          method: "DELETE",
          body: {
            ...(input.leftOn ? { left_on: input.leftOn } : {}),
            ...(input.reason?.trim() ? { reason: input.reason.trim() } : {}),
            ...(input.correction ? { correction: true } : {}),
            ...(input.force ? { force: true } : {}),
          },
        }
      )

      return toAppointment(data)
    },
    onSuccess: (appointment) => {
      store(queryClient, [appointment])
    },
  })
}

function toUpdatePayload(input: UpdateAppointmentInput) {
  return {
    ...(input.jobTitle !== undefined
      ? { job_title: input.jobTitle === null ? null : blank(input.jobTitle) }
      : {}),
    ...(input.isContact !== undefined ? { is_contact: input.isContact } : {}),
    ...(input.functions
      ? {
          functions: input.functions.map((held) => ({
            ...(held.id ? { id: held.id } : {}),
            involvement_type: held.involvementType,
            ...(held.startedOn !== undefined
              ? { started_on: blank(held.startedOn) }
              : {}),
            ...(held.approvedOn !== undefined
              ? { approved_on: blank(held.approvedOn) }
              : {}),
            ...(held.endedOn !== undefined
              ? { ended_on: blank(held.endedOn) }
              : {}),
          })),
        }
      : {}),
    ...(input.startedOn !== undefined
      ? { started_on: blank(input.startedOn) }
      : {}),
    ...(input.endedOn !== undefined ? { ended_on: blank(input.endedOn) } : {}),
    ...(input.leftOn ? { left_on: input.leftOn } : {}),
    ...(input.approvedOn ? { approved_on: input.approvedOn } : {}),
    ...(input.firmId ? { firm_id: input.firmId } : {}),
    ...(input.reason?.trim() ? { reason: input.reason.trim() } : {}),
    ...(input.correction ? { correction: true } : {}),
    ...(input.force ? { force: true } : {}),
  }
}
